// Pure builder for the offline outbox record. Extracted from
// offline.js/saveSubmissionOffline so it can be unit-tested without IndexedDB
// or the browser globals. offline.js passes the browser-derived values in.
export function buildOfflineRecord(input) {
  return {
    queuedAt:       Date.now(),
    attempts:       0,
    signed:         !!input.signed,
    orgId:          input.orgId || null,
    pdf:            input.pdf,
    pdfFilename:    input.pdfFilename,
    submissionType: input.submissionType,
    project:        input.project,
    foremanName:    input.foremanName,
    date:           input.date,
    workersOnSite:  input.workersOnSite || '',
    sendToSafety:   !!input.sendToSafety,
    csoContact:     input.csoContact || null,
    // Who filed the form — always the signed-in account.
    userId:         input.effUserId || null,
    userName:       input.effUserName || '',
    // Production Report only: the subcontract crew the units belong to, when an
    // admin entered a sub's week. Separate from the author on purpose — the
    // replay costs the production to this crew without changing whose form it
    // is. Null on every other form and on records queued before this existed.
    crewForemanId:   input.crewForemanId   ?? null,
    crewForemanName: input.crewForemanName ?? '',
    clientUuid:     input.clientUuid || (globalThis.crypto?.randomUUID?.() || String(Date.now())),
    formKey:        input.formKey || null,
    schemaId:       input.schemaId || null,
    schemaVersion:  input.schemaVersion || null,
    // Trade pack in effect at queue time (Task 4 follow-up) — the replay
    // stamps it on the eventual submissions row exactly like the online path.
    // Defaults to null so pre-existing queued records (built before this
    // field existed) replay unaffected.
    tradePack:      input.tradePack ?? null,
    data:           input.data || {},
    // Whether this form type emails the safety inbox (PWA item 2). Decided at
    // QUEUE time because the service worker's background replay has no access
    // to app.js's SAFETY_EMAIL_TYPES list. undefined on records queued before
    // this field existed — the worker skips those and the app-open replay
    // (which recomputes it live) handles them exactly as before.
    emailsSafety:   typeof input.emailsSafety === 'boolean' ? input.emailsSafety : undefined,
    // Structured forms (Production Report / Weekly Timesheet, Onda 2b): the
    // replay reconstructs `s` from these to run the idempotent per-day sync.
    syncKind:       input.syncKind || null,
    // Sync-only repair record: the submission row + PDF already exist (the
    // online submit landed them) — only the per-day money rows are missing.
    // The replay skips insert/upload/email and runs just the structured sync
    // against this existing submission. Built by saveSyncRepairOffline when an
    // online sync fails, and by the self-check watchdog when it finds a filed
    // week whose numbers never arrived.
    syncOnly:       !!input.syncOnly,
    submissionId:   input.submissionId ?? null,
    // The Monday of the week this filing is FOR. The replay used to insert the
    // submission without it, so an offline weekly form landed with week_of
    // NULL and the repair path fell back to submitted_at — the reconnect
    // moment, not the work — writing a whole week of money onto the wrong
    // dates, permanently and invisibly (rows existed, so nothing rechecked).
    weekOf:         input.weekOf ?? null,
    // The add-vs-replace answer, when the person gave one for THIS filing.
    // Deliberately a field of the record and not a key in `fields`: inside the
    // payload it was persisted to submissions.data and to the draft, and a
    // stale 'week' answer replayed days later destroys another filing.
    weekMode:       input.weekMode ?? null,
    fields:         input.fields ?? null,
    // Site Photos (Onda 2c) + Extra Work Order (Onda 2d): compressed photo blobs
    // the replay uploads.
    photos:         input.photos ?? null,
    // Extra Work Order (Onda 2d): the two signatures the parent EWO row stores.
    // Null for every other form (their signature is baked into the PDF already).
    signature:      input.signature   ?? null,
    gcSignature:    input.gcSignature ?? null,
  };
}

// ── Clock punches (check in / check out) ─────────────────────────────────────
// What the field app queues when a tap cannot reach the server, the order the queue
// replays in, and what a server refusal means for a queued punch. Pure like the
// builder above: no IndexedDB, no Supabase, so it unit-tests without a browser.

// The outbox record of a punch, a time-off request or a decision on one (contract
// 3.1). `punch` is the INSERT payload of time_punches. It never carries sent_at (the
// replay stamps it when it sends) nor punched_by / org_id (the server owns them).
export function buildClockRecord(input) {
  const { syncKind, punch, request, requestId } = input;
  let clientUuid;
  let own;
  if (syncKind === 'punch') {
    clientUuid = punch?.id;
    own = {
      punch:       { ...punch, offline: true },
      workerId:    punch?.worker_id,
      punchedAt:   punch?.punched_at,
      projectName: input.projectName ?? null,
      workerName:  input.workerName ?? null,   // whose punch: a crew punch's toasts and waiting text name him
      pair:        input.pair ?? null, // 'move' on both halves of out-A + in-B (one punched_at)
    };
  } else if (syncKind === 'timeoff') {
    clientUuid = request?.id;
    own = { request };
  } else if (syncKind === 'timeoff_decision') {
    clientUuid = requestId;
    own = { requestId, decision: input.decision };
  } else {
    throw new TypeError(`buildClockRecord: unknown syncKind "${syncKind}"`);
  }
  if (!clientUuid) throw new TypeError(`buildClockRecord: a ${syncKind} record needs an id`);
  if (syncKind === 'punch') {
    // orderPunchRecords sorts on these two before anything is sent: refuse the record here
    for (const field of ['worker_id', 'punched_at']) {
      if (!punch[field]) throw new TypeError(`buildClockRecord: a punch record needs a ${field}`);
    }
  }
  return {
    queuedAt:       Date.now(),
    attempts:       0,
    stuck:          false,
    waiting:        null,
    orgId:          input.orgId || null,
    userId:         input.userId || null,
    userName:       input.userName || '',
    syncKind,
    pdf:            null,
    signed:         false,
    submissionType: null,
    clientUuid,
    ...own,
  };
}

const orderKey = (value) => String(value ?? '');

// The order punch records replay in (contract 3.3): one worker's punches stay together,
// in the order he made them. Punches of one instant (a Move's out and in; a check-in and
// its Undo, a void copies its target's punchedAt) are told apart by the outbox id: the
// IndexedDB autoincrement, i.e. the order of the taps, which never steps back. queuedAt is
// the wall clock, which a phone can re-sync backwards between two taps, so it only breaks
// a tie of ids. A record that replaces a punch still in the queue (a day sheet's fix of a queued
// punch, an Undo) sorts at the LATER of its own time and its target's: a fix moved to an EARLIER
// time would otherwise replay first, find no target (23503 on replaces_id: "Already handled", the
// fix dropped) and let the target land unchanged. The target was queued first, so the tie goes to
// its lower id. A key a record lacks reads as empty / 0, so one malformed record can never
// make the sort throw and freeze the whole queue. Returns a new array; pass punch records only.
export function orderPunchRecords(recs) {
  const byPunch = new Map(recs.filter((r) => r.punch?.id).map((r) => [r.punch.id, r]));
  // `seen` ends a loop of replaces_id (malformed records): each record is entered once per walk
  const sortTime = (r, seen = new Set()) => {
    const own = orderKey(r.punchedAt), target = byPunch.get(r.punch?.replaces_id);
    if (!target || seen.has(r)) return own;
    const its = sortTime(target, seen.add(r));
    return own > its ? own : its;
  };
  const time = new Map(recs.map((r) => [r, sortTime(r)]));
  return [...recs].sort((a, b) =>
    orderKey(a.workerId).localeCompare(orderKey(b.workerId))
    || time.get(a).localeCompare(time.get(b))
    || (a.id ?? 0) - (b.id ?? 0)
    || (a.queuedAt ?? 0) - (b.queuedAt ?? 0));
}

const NEEDS_FOREMAN = '1 punch needs your foreman';
const CREW_NOT_SAVED = '1 crew punch could not be saved — tap to see';
// A time-off request the server refused for good (42501: the crewman left the crew, an unlinked worker; 23514):
// waiting for nobody, so the words send him to the row, where Cancel takes it off the phone
const TIMEOFF_REFUSED = 'Time off request refused — cancel it on Time off and ask your admin';
const TIMEOFF_REFUSED_PILL = '1 time off request refused — see Time off';

// What a server refusal means for a queued record (contract 3.4). `error` is the
// PostgREST error { code, message, details, hint }; `details` carries the trigger's
// DETAIL json. Returns { action, detail, ... } with action one of
//   done        the row is already there                    -> drop the record
//   done_toast  somebody else already did it                -> drop it, show `toast`
//   stuck_now   only a foreman can fix it                   -> keep it: waiting 'foreman',
//               `waitingText` is the toast, `pillText` the offline pill line
//   wait        the week is closed                          -> keep it: waiting 'week_closed'
//   retry       anything else                               -> count an attempt, unless `server: true`:
//               the SERVER failed, not the punch (a 5xx, a gateway's error page, a statement timeout),
//               which counts no attempt, as a lost line does
// `fmtTime` makes the H:MM of a timestamp (Ordinex.timeClock.fmtTime); it is passed in
// so this file does not import time-clock.js. `status` is the HTTP status of the response
// supabase-js hands back next to { error } (a gateway's HTML page has no code, only a status).
export function classifyPunchError(error, rec, fmtTime = String, status = 0) {
  const code = error?.code;
  const message = String(error?.message ?? '');
  const details = String(error?.details ?? '');
  const text = message + ' ' + details;
  const has = (word) => message.includes(word);
  let detail = {};
  try {
    const parsed = JSON.parse(details);
    if (parsed && typeof parsed === 'object') detail = parsed;
  } catch { /* plain-text DETAIL, e.g. 'Key (id)=(...) already exists.' */ }
  const by = detail.by_name || 'your foreman';
  const at = () => fmtTime(detail.punched_at);
  // A punch the foreman made for a worker (a crew punch, or a day sheet's correction): he is the one
  // reading, so the words name the worker (rec.workerName) and never send him to "your foreman" — a
  // refusal of one of his punches is "could not be saved", for him to try again or discard.
  const source = rec?.punch?.source;
  const crew = source === 'crew';
  const forCrew = crew || source === 'correction';
  const name = rec?.workerName || 'the worker';
  const Name = name.charAt(0).toUpperCase() + name.slice(1);
  const someone = detail.by_name || 'Someone';
  // the DETAIL names who punched; when that is the worker himself it reads "Ana already checked in"
  // ponytail: told by the name (by_name is profiles.full_name, workerName workers.name), the DETAIL carries no punched_by
  const himself = !!detail.by_name && detail.by_name.trim().toLowerCase() === name.trim().toLowerCase();
  const needsForeman = (waitingText = NEEDS_FOREMAN, pillText = NEEDS_FOREMAN) => (rec?.syncKind === 'timeoff'
    ? { action: 'stuck_now', waiting: 'foreman', waitingText: TIMEOFF_REFUSED, pillText: TIMEOFF_REFUSED_PILL }
    : forCrew
    ? { action: 'stuck_now', waiting: 'foreman', waitingText: `${Name}'s punch could not be saved`, pillText: CREW_NOT_SAVED }
    : { action: 'stuck_now', waiting: 'foreman', waitingText, pillText });
  const alreadyHandled = () => ({ action: 'done_toast', toast: `Already handled by ${by}` });

  // The rows of the contract table, in its order: the first match wins, so a
  // duplicate primary key is never read as the replaces_id race below it.
  const rows = [
    [code === '23505' && /time_punches_pkey|time_off_requests_pkey/.test(text),
      () => ({ action: 'done' })],
    // a crew punch: what the server already holds is the answer, said about the worker
    [crew && has('already_in') && detail.project_id === rec.punch.project_id,
      () => ({ action: 'done_toast', toast: himself ? `${Name} already checked in at ${at()}` : `${someone} already checked ${name} in at ${at()}` })],
    [crew && has('already_in'),   // on another job: the crew card shows it, with Move here when it is his
      () => ({ action: 'done_toast', toast: `${Name} is checked in at ${detail.project_name} since ${at()}` })],
    [crew && has('already_out'),
      () => ({ action: 'done_toast', toast: himself ? `${Name} already checked out at ${at()}` : `${someone} already checked ${name} out at ${at()}` })],
    [crew && rec.punch.kind === 'out' && has('not_in'),   // the DETAIL names his open shift, if any: on another job, that one
      () => ({ action: 'done_toast', toast: detail.open_project_id && detail.open_project_id !== rec.punch.project_id
        ? `${Name} is checked in at ${detail.open_project_name}, not here.` : `${Name} is not checked in.` })],
    [has('already_in') && detail.project_id === rec?.punch?.project_id,
      () => ({ action: 'done_toast', toast: `${by} already checked you in at ${at()}` })],
    [has('already_in'),
      () => needsForeman(`You're checked in at ${detail.project_name} since ${at()} — ask your foreman`,
        `1 punch needs your foreman — you're checked in at ${detail.project_name} since ${at()}`)],
    [has('already_out'),
      () => ({ action: 'done_toast', toast: `${by} set your check-out at ${at()}` })],
    [has('not_in') || has('not_on_break'),
      () => needsForeman()],
    [code === '23505' && text.includes('time_punches_replaces_id_key'),
      () => ({ action: 'retry' })],
    [code === '23503' && text.includes('replaces_id'),
      alreadyHandled],
    // any other foreign key: the job (or the worker) was deleted and its punches went with it
    [code === '23503',
      () => ({ action: 'done_toast', toast: 'That project was deleted — this punch was not saved.' })],
    [has('already_decided'),
      () => ({ action: 'done_toast',
        toast: `Already ${detail.status} by ${by} on ${new Date(detail.decided_at).toLocaleDateString('en-CA', { weekday: 'long' })}` })],
    [has('week_closed'),
      () => ({ action: 'wait', waiting: 'week_closed' })],
    // a time-off decision into a payroll-final week: refused until the office reopens it, so it waits like a closed week
    [has('time_off_week_final'),
      () => ({ action: 'wait', waiting: 'week_closed' })],
    [has('void_not_own_last'),
      alreadyHandled],
    // RLS refusal: an unlinked worker, a target out of scope, a self correction
    [code === '42501',
      () => needsForeman(rec?.punch?.source === 'self' ? 'Ask your admin to link your account' : null)],
    [has('bad_target') || has('reason_required') || code === '23514',
      () => needsForeman()],
    [true,
      () => (status >= 500 || code === '57014' ? { action: 'retry', server: true } : { action: 'retry' })],
  ];
  const [, build] = rows.find(([hit]) => hit);
  return { ...build(), detail };
}
